import { Matrix4, Quaternion, Vector3 } from "three";
import type { BodyTable } from "../sim/body-table";
import type { SolarSystem } from "../sim/solar-system";
import { approach, dampingFor, focusDistanceFor, smoothstep } from "./animation";
import { FrameUpdater, type FrameData } from "./frame";
import { ANCHOR_ANGLE, framingAngle, framingOffset } from "./framing";
import { pickNearest } from "./picking";
import { projectAll, projectRadii } from "./project";
import { createRenderer, type RendererHandle } from "./renderer";
import { ScaleMapper, paramsFor, type ScaleMode, type ScaleParams } from "./scale-mapper";
import { TextureManager } from "./textures";
import { InstancedTier } from "./tiers/instanced";
import { OrbitsTier } from "./tiers/orbits";
import { PointsTier } from "./tiers/points";
import { TexturedTier } from "./tiers/textured";
import type { Tier } from "./tiers/tier";
import { SMALL_MOON_SYSTEM, fillMoonVisibility, fillShown, type ShownInput } from "./visibility";

const SCALE_ANIMATION_S = 2;
const FOCUS_RATE = 5;
const PICK_MIN_PX = 8;
const ORBIT_PICK_PX = 6;
const SWING_DONE_RAD = 1e-4;
const DAMPING_AT_60_FPS = 0.08;

const vp = new Matrix4();
const camLocal = new Vector3();
const cameraOffset = new Vector3();
const anchorNow = new Vector3();
const framed = new Vector3();
const turn = new Quaternion();
const swing = new Quaternion();
const step = new Quaternion();

/** The drawing buffer in device pixels and what three.js holds on the GPU. */
export interface RenderInfo {
  width: number;
  height: number;
  pixelRatio: number;
  textures: number;
  geometries: number;
}

export class View {
  readonly mapper = new ScaleMapper();
  focus = 0;
  readonly screen: Float32Array;
  readonly radiiPx: Float32Array;
  readonly frameData: FrameData;
  readonly textures = new TextureManager();

  private readonly handle: RendererHandle;
  private readonly updater: FrameUpdater;
  private readonly tiers: Tier[];
  private readonly orbits: OrbitsTier;
  private readonly table: BodyTable;
  private readonly shownInput: ShownInput;
  private readonly overviewKm: number;
  private targetDistance: number | null = null;
  /** The body kept in frame above the focus: its parent, or -1 for the Sun overview. */
  private anchor = -1;
  private readonly anchorDir = new Vector3();
  private swinging = false;
  private scaleFrom: ScaleParams | null = null;
  private scaleTo: ScaleParams | null = null;
  private scaleT = 0;
  private lost = false;

  constructor(
    canvas: HTMLCanvasElement,
    private readonly sim: SolarSystem,
  ) {
    const textures = this.textures;
    this.handle = createRenderer(canvas, textures);
    this.updater = new FrameUpdater(sim, this.mapper);
    this.frameData = this.updater.data;
    const table = (this.table = sim.table);
    const { bodies } = table;
    const rootId = bodies[table.root].id;
    this.overviewKm = Math.max(
      ...bodies
        .filter((b) => b.parent === rootId && b.orbit)
        .map((b) => b.orbit!.a * (1 + b.orbit!.e)),
    );
    this.shownInput = {
      count: sim.count,
      tier: table.tier,
      parentIndex: table.parentIndex,
      orbitKm: table.orbitKm,
      positions: this.frameData.positions,
      camera: camLocal,
      focus: 0,
      hover: -1,
    };
    this.screen = new Float32Array(3 * sim.count);
    this.radiiPx = new Float32Array(sim.count);
    this.orbits = new OrbitsTier(table, this.mapper);
    this.tiers = [
      new TexturedTier(bodies, table.byTier.textured, textures, (km) =>
        this.mapper.mapDistance(km),
      ),
      new InstancedTier(bodies, table.byTier.simple),
      new PointsTier(bodies, table.byTier.point),
      this.orbits,
    ];
    for (const t of this.tiers) this.handle.world.add(t.object);
    // a drag during the fly-in wins; the anchor lock then keeps the angle it leaves
    this.handle.controls.addEventListener("start", () => {
      this.swinging = false;
    });
    canvas.addEventListener("webglcontextlost", (e) => {
      e.preventDefault();
      this.lost = true;
    });
    canvas.addEventListener("webglcontextrestored", () => {
      this.lost = false;
    });
    this.mapper.set(paramsFor("visual"));
  }

  get contextLost(): boolean {
    return this.lost;
  }
  viewportWidth(): number {
    return this.handle.renderer.domElement.clientWidth;
  }
  viewportHeight(): number {
    return this.handle.renderer.domElement.clientHeight;
  }
  drawCalls(): number {
    return this.handle.renderer.info.render.calls;
  }
  renderInfo(): RenderInfo {
    const { renderer } = this.handle;
    const { memory } = renderer.info;
    return {
      width: renderer.domElement.width,
      height: renderer.domElement.height,
      pixelRatio: renderer.getPixelRatio(),
      textures: memory.textures,
      geometries: memory.geometries,
    };
  }
  cameraDistance(): number {
    return this.handle.camera.position.length();
  }

  distanceTo(index: number): number {
    const p = this.frameData.positions,
      c = this.frameData.cameraPosition,
      o = 3 * index;
    return Math.hypot(p[o] - c.x, p[o + 1] - c.y, p[o + 2] - c.z);
  }

  setFocus(index: number): void {
    this.focus = index;
    // FrameUpdater keeps the followed body at the origin as it moves. Re-anchor
    // OrbitControls there too, including after a pan or reselecting the same body.
    const { camera, controls } = this.handle;
    const damping = controls.enableDamping;
    controls.enableDamping = false;
    controls.update();
    camera.position.sub(controls.target);
    controls.target.set(0, 0, 0);
    controls.update();
    controls.enableDamping = damping;
    this.targetDistance = this.targetDistanceFor(index, this.scaleTo ?? this.mapper.params);
    // the direction is read on the next frame: at startup the sim has no positions yet
    this.anchor = this.table.parentIndex[index];
    this.anchorDir.set(0, 0, 0);
    this.swinging = this.anchor >= 0;
  }

  /**
   * Writes the unit direction from the focus to its anchor in scene axes and
   * returns the distance in km, 0 before the sim has positions. The scale mapping
   * only shortens each body's offset from its own parent, so km give the direction.
   */
  private anchorDirection(out: Vector3): number {
    const p = this.sim.positions,
      a = 3 * this.anchor,
      f = 3 * this.focus;
    out.set(p[a] - p[f], p[a + 1] - p[f + 1], p[a + 2] - p[f + 2]);
    const km = out.length();
    if (km > 0) out.applyQuaternion(this.handle.world.quaternion).divideScalar(km);
    return km;
  }

  /** Turns the camera with the focus→anchor line and, after a selection, swings it into the framing. */
  private followAnchor(dt: number): void {
    const { camera, controls } = this.handle;
    const anchorKm = this.anchorDirection(anchorNow);
    if (anchorKm === 0) return;
    if (this.anchorDir.lengthSq() > 0) {
      turn.setFromUnitVectors(this.anchorDir, anchorNow);
      camera.position.applyQuaternion(turn);
      controls.target.applyQuaternion(turn);
    }
    this.anchorDir.copy(anchorNow);
    if (!this.swinging) return;
    const offset = cameraOffset.copy(camera.position).sub(controls.target);
    const length = offset.length();
    offset.divideScalar(length);
    // aim for where the zoom ends so a near anchor, like a moon's planet, lands at the angle
    const ratio = (this.targetDistance ?? length) / this.mapper.mapDistance(anchorKm);
    framingOffset(anchorNow, camera.up, framingAngle(ANCHOR_ANGLE, ratio), framed);
    if (offset.angleTo(framed) < SWING_DONE_RAD) {
      offset.copy(framed);
      this.swinging = false;
    } else {
      swing.setFromUnitVectors(offset, framed);
      offset.applyQuaternion(step.identity().slerp(swing, 1 - Math.exp(-FOCUS_RATE * dt)));
    }
    camera.position.copy(controls.target).addScaledVector(offset, length);
  }

  private targetDistanceFor(index: number, params: ScaleParams): number {
    // the root focus is the overview: every planet orbit fits on screen
    if (index === this.table.root)
      return 1.25 * Math.pow(this.overviewKm * params.distanceGain, params.distanceExponent);
    return focusDistanceFor(params.radiusGain * this.table.radiusKm[index]);
  }

  setScaleMode(mode: ScaleMode): void {
    this.scaleFrom = { ...this.mapper.params };
    this.scaleTo = paramsFor(mode);
    this.scaleT = 0;
    this.targetDistance = this.targetDistanceFor(this.focus, this.scaleTo);
  }

  setOrbitsVisible(visible: boolean): void {
    this.orbits.object.visible = visible;
  }
  setHover(index: number): void {
    this.orbits.hoverBody = index;
    this.shownInput.hover = index;
  }

  /** Body under (x, y), else the body whose orbit line passes nearby, else -1. */
  pick(x: number, y: number, minRadiusPx = PICK_MIN_PX): number {
    const body = pickNearest(
      this.screen,
      this.radiiPx,
      this.sim.count,
      x,
      y,
      minRadiusPx,
      this.frameData.visible,
    );
    if (body >= 0) return body;
    // vp still holds the matrix of the last drawn frame, which the orbit vertices belong to
    return this.orbits.pick(
      vp.elements,
      this.viewportWidth(),
      this.viewportHeight(),
      x,
      y,
      Math.max(ORBIT_PICK_PX, minRadiusPx / 2),
    );
  }

  frame(dt: number): void {
    const { camera, controls, world, scene } = this.handle;
    if (this.scaleFrom && this.scaleTo) {
      this.scaleT += dt / SCALE_ANIMATION_S;
      if (this.scaleT >= 1) {
        this.mapper.set(this.scaleTo);
        this.scaleFrom = this.scaleTo = null;
      } else this.mapper.lerp(this.scaleFrom, this.scaleTo, smoothstep(this.scaleT));
    }
    if (this.anchor >= 0) this.followAnchor(dt);
    if (this.targetDistance !== null) {
      const offset = cameraOffset.copy(camera.position).sub(controls.target);
      const current = offset.length();
      const next = approach(current, this.targetDistance, dt, FOCUS_RATE);
      camera.position.copy(controls.target).add(offset.setLength(next));
      if (Math.abs(next - this.targetDistance) < 1e-3 * this.targetDistance)
        this.targetDistance = null;
    }
    const focusRadius = this.mapper.mapRadius(this.table.radiusKm[this.focus]);
    controls.minDistance = focusRadius * 1.5;
    controls.dampingFactor = dampingFor(DAMPING_AT_60_FPS, dt);
    controls.update();
    const dist = camera.position.length();
    camera.near = Math.max(1e-6, dist * 1e-4);
    camera.updateProjectionMatrix();

    scene.updateMatrixWorld();
    camLocal.copy(camera.position);
    world.worldToLocal(camLocal);
    const data = this.updater.update(
      this.focus,
      camLocal,
      this.viewportHeight(),
      (camera.fov * Math.PI) / 180,
    );
    data.pixelRatio = this.handle.renderer.getPixelRatio();
    camera.updateMatrixWorld();

    vp.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse).multiply(
      world.matrixWorld,
    );
    projectAll(
      data.positions,
      data.count,
      vp.elements,
      this.viewportWidth(),
      this.viewportHeight(),
      this.screen,
    );
    projectRadii(
      data.radii,
      this.screen,
      data.count,
      this.viewportHeight(),
      data.fovRad,
      this.radiiPx,
    );
    fillMoonVisibility(
      this.table.moonParent,
      this.table.moonCount,
      this.table.planets,
      this.screen,
      this.radiiPx,
      this.viewportWidth(),
      this.viewportHeight(),
      this.focus,
      data.visible,
    );
    this.shownInput.focus = this.focus;
    fillShown(this.shownInput, this.mapper, data.shown);
    for (let k = 0; k < data.count; k++) {
      const parent = this.table.moonParent[k];
      if (!data.visible[k]) data.shown[k] = 0;
      else if (parent >= 0 && this.table.moonCount[parent] < SMALL_MOON_SYSTEM) data.shown[k] = 1;
    }
    this.orbits.setResolution(this.viewportWidth(), this.viewportHeight());
    for (const t of this.tiers) t.update(data);
    if (!this.lost) this.handle.render();
  }
}
